import type { Rng } from '../../core/rng';
import { linearToLab, luminance, type LinearRGB } from '../../core/color';
import type { PaletteModel, PaletteClass } from '../../data/types';
import type { Chimney, GableType, FrontColour, FrontType, RoofMaterial, HouseParams, Lot, LotKind } from './types';
import { settingOf, type HouseSetting } from './houseSetting';

type Shares = Readonly<Record<HouseSetting, number>>;

/* Sources and measurements for every figure below: docs/plans/houses.md. */
export const HOUSE = {
  storeyHeight: { mean: 2.9, sd: 0.2 },
  /**
   * A one-storey house is a hall house (zaalhuis) with one tall room: Zantkuijl (Lamp van Diogenes
   * 78, 1983) gives 3-3.5 m for the back room and 3.5 m for a front room on the main floor.
   */
  hallHeight: { mean: 3.6, sd: 0.25 },
  // STYLE.md §8: pitch 61.9-63.9° measured after foreshortening correction
  pitch: { min: 62, max: 64 },
  /**
   * The pitch was read on the painting's gables, nearly all under 8 m wide. A gable wider than this
   * keeps the rise of one this wide, at a gentler pitch, rather than climbing to a pyramid over a 15 m
   * front (the giant roof of the user's screenshot, docs/plans/houses.md, "Wide fronts"). The 8 m is an
   * estimate: no source gives the widest single span, and 15 of the 3,411 house lots are wider.
   */
  span: { maxGable: 8 },
  /**
   * A back building narrower than this is a shed, a privy or a store: one storey, no chimney. The ones
   * recorded measured 6.50 by 4.10 m (AAR 60), so none narrower than 4 m is taken for a house of two
   * storeys with a hearth; the width is an estimate, and 548 of 2,608 back buildings are narrower.
   */
  shedWidth: 4,
  /**
   * The 1544 woodcut: 59% of the one-storey houses and 51.8% of all houses have timber fronts
   * (Amsterdam Monumentenstad, "Houten gevels"), so 38.6% of the higher ones.
   */
  timber: { all: 0.518, singleStorey: 0.59, higherStorey: 0.386 },
  /**
   * Brick fronts were the richer houses' (Meischke, Jaarboek Monumentenzorg 1996): fewer timber
   * fronts on the main streets, more in the Lastage, keeping the city's 51.8%.
   */
  timberFactor: { main: 0.8, other: 1.0, lastage: 1.2 } satisfies Shares,
  /**
   * STYLE §8: the painting's gable walls are pale olive-cream (wallLight) but for a few brick ones
   * (wallBrick), which the fabric has drawn at 0.15 of all fronts. The brick-coloured fronts are
   * brick fronts; the other brick fronts wear the painting's pale colour, as 16th-century brick
   * took ochre and stone colours (Hendrick de Keyser, "Architectuur en kleur").
   */
  brickColouredFronts: 0.15,
  /**
   * Share of brick fronts with stepped gables. The painting cannot tell a stepped gable from a pointed
   * one; on the woodcut about 6 of 21 street-end gables of the Nieuwendijk's west row are stepped,
   * and few of the back streets' (docs/plans/houses.md, "Gables").
   */
  steppedOfBrick: { main: 0.5, other: 0.15, lastage: 0.2 } satisfies Shares,
  /**
   * Roof shares from the painting's roofs by district (docs/plans/houses.md, "Roofs"). No roof is
   * dark: the palette measures roofDark on roof slopes in shade, which the renderer shades from tile
   * and slate itself, so the round-1 dark shares went to tile and slate in proportion.
   */
  /**
   * The share of its weight a tile roof's highlight centre (roofTile #967033, above the class's p90 in
   * luminance) is drawn at. The renderer lights a roof's sunward slope itself: a #87571f roof's lit
   * third renders at L* 51, a* 8.5, b* 34.7, the highlight's own colour, and a roof based on the
   * highlight renders its lit third at L* 55, a* 6, as street ground. Half weight gives the least band
   * class TV of 0, 0.25, 0.5 and 1 on the painter view (docs/plans/houses.md, section 8.3).
   */
  tileHighlight: 0.5,
  /**
   * The greenest CIELAB hue, in degrees, a wall's colour may have. The painting's house walls measure 83-88
   * degrees on the museum scan (docs/plans/houses.md, section 8.7); wallLight's #81723e, at 93, reads
   * olive beside them.
   */
  wallHueMax: 90,
  roofWeights: {
    main: { tile: 0.6, slate: 0.3 },
    other: { tile: 0.52, slate: 0.36 },
    lastage: { tile: 0.5, slate: 0.3 },
  },
  /**
   * The 1544 woodcut draws chimneys on nearly every house roof, mostly two, a plain stack and a
   * stack flaring to its top, about a third and two thirds of the way back (docs/plans/houses.md,
   * "Chimneys"). A back building, a kitchen or a store, keeps one plain stack at mid-depth at the
   * fabric's old rate.
   */
  chimneys: {
    house: { counts: [0, 1, 2], weights: [0.1, 0.1, 0.8] },
    backhouse: 0.6,
    flared: 0.5,
    at: { 1: [0.5], 2: [1 / 3, 2 / 3] },
  },
  steppedGable: { stepRise: 1.1, minSteps: 4, maxSteps: 7, wallThickness: 0.3, overhang: 0.4 },
  spoutGable: { neckWidthRatio: 0.35, shoulderRatio: 0.6, neckTopRatio: 0.85 },
} as const;

/**
 * Storeys under the roof: the 1544 woodcut shows 64.5% of the houses with one storey
 * (Amsterdam Monumentenstad, "Houten gevels"); the main streets build higher, the Lastage lower.
 */
export const HOUSE_STOREY_WEIGHTS: Readonly<Record<HouseSetting, { storeys: number[]; weights: number[] }>> = {
  main: { storeys: [1, 2, 3], weights: [0.45, 0.43, 0.12] },
  other: { storeys: [1, 2, 3], weights: [0.66, 0.3, 0.04] },
  lastage: { storeys: [1, 2, 3], weights: [0.8, 0.2, 0] },
};

const BACKHOUSE_STOREYS = { storeys: [1, 2], weights: [0.7, 0.3] };

export function sampleStoreys(kind: LotKind, setting: HouseSetting, rng: Rng): number {
  const { storeys, weights } = kind === 'backhouse' ? BACKHOUSE_STOREYS : HOUSE_STOREY_WEIGHTS[setting];
  return rng.weighted(storeys, weights);
}

export function sampleStoreyHeight(rng: Rng): number {
  return rng.normal(HOUSE.storeyHeight.mean, HOUSE.storeyHeight.sd);
}

export function sampleHallHeight(rng: Rng): number {
  return rng.normal(HOUSE.hallHeight.mean, HOUSE.hallHeight.sd);
}

export function samplePitch(rng: Rng): number {
  const degrees = rng.range(HOUSE.pitch.min, HOUSE.pitch.max);
  return (degrees * Math.PI) / 180;
}

export function sampleFrontType(storeys: number, setting: HouseSetting, rng: Rng): FrontType {
  const base = storeys === 1 ? HOUSE.timber.singleStorey : HOUSE.timber.higherStorey;
  return rng.chance(Math.min(0.95, base * HOUSE.timberFactor[setting])) ? 'timber' : 'brick';
}

const BRICK_COLOURED_OF_BRICK = HOUSE.brickColouredFronts / (1 - HOUSE.timber.all);

export function sampleFrontColour(front: FrontType, rng: Rng): FrontColour {
  return front === 'brick' && rng.chance(BRICK_COLOURED_OF_BRICK) ? 'brick' : 'light';
}

/** A timber front ends in a pointed gable; a brick one is pointed or stepped. */
export function sampleFrontGable(front: FrontType, setting: HouseSetting, rng: Rng): GableType {
  if (front === 'timber') return 'plain';
  return rng.chance(HOUSE.steppedOfBrick[setting]) ? 'stepped' : 'plain';
}

/**
 * A roof takes the material its block side's painted share gave it where the painting is legible
 * there (painted/paintedRoofs.ts), else a draw at its setting's shares; the draw is taken either way,
 * so no other parameter moves.
 */
export function sampleRoofMaterial(setting: HouseSetting, rng: Rng, painted: RoofMaterial | null = null): RoofMaterial {
  if (painted !== null) {
    rng.next();
    return painted;
  }
  const { tile, slate } = HOUSE.roofWeights[setting];
  return rng.weighted<RoofMaterial>(['tile', 'slate'], [tile, slate]);
}

/* The forms come from their own fork, so the chimneys take one draw from the house's stream, as the single stack did, and no other parameter of any house changes with them. */
export function sampleChimneys(kind: LotKind, rng: Rng): readonly Chimney[] {
  const { house, backhouse, flared, at } = HOUSE.chimneys;
  if (kind === 'backhouse') return rng.chance(backhouse) ? [{ at: at[1][0], form: 'stack' }] : [];
  const count = rng.weighted(house.counts, house.weights);
  if (count === 0) return [];
  const forms = rng.fork('chimneyForms');
  return at[count === 1 ? 1 : 2].map((a): Chimney => ({ at: a, form: forms.chance(flared) ? 'flared' : 'stack' }));
}

/* A back building's one storey is a kitchen or a store, not a hall. */
function storeyHeightOf(kind: LotKind, storeys: number, rng: Rng): number {
  return kind === 'house' && storeys === 1 ? sampleHallHeight(rng) : sampleStoreyHeight(rng);
}

/** What the sources show of one lot: its roof on the painting, its front gable on the woodcut; null where not legible. */
export interface LotReadings {
  readonly roof: RoofMaterial | null;
  readonly gable: GableType | null;
}

export const NO_READINGS: LotReadings = { roof: null, gable: null };

/*
 * The woodcut's gable replaces the drawn one after every draw is taken, so no other parameter moves.
 * A stepped gable stands on a brick front, and a timber front drew no brick colour, so its colour
 * comes from a fork of the lot's own stream.
 */
function withWoodcutGable(params: HouseParams, gable: GableType | null, rng: Rng): HouseParams {
  if (gable === null) return params;
  if (gable !== 'stepped') return { ...params, frontGable: gable };
  const frontColour = params.frontType === 'brick' ? params.frontColour : sampleFrontColour('brick', rng.fork('woodcutFront'));
  return { ...params, frontType: 'brick', frontGable: 'stepped', frontColour };
}

export function sampleHouseParams(
  lot: Pick<Lot, 'kind' | 'sourceId' | 'reconstruction'>,
  rng: Rng,
  readings: LotReadings = NO_READINGS,
  /** The building's width where it is known: a wide gable is lowered and a narrow back building is a shed. */
  width?: number
): HouseParams {
  const setting = settingOf(lot.sourceId);
  const storeys = sampleStoreys(lot.kind, setting, rng);
  const frontType = sampleFrontType(storeys, setting, rng);
  const params: HouseParams = {
    storeys,
    storeyHeight: storeyHeightOf(lot.kind, storeys, rng),
    pitch: samplePitch(rng),
    frontGable: lot.kind === 'house' ? sampleFrontGable(frontType, setting, rng) : 'plain',
    backGable: 'plain',
    chimneys: sampleChimneys(lot.kind, rng),
    frontType,
    frontColour: sampleFrontColour(frontType, rng),
    roofMaterial: sampleRoofMaterial(setting, rng, readings.roof),
  };
  const read = lot.kind === 'house' ? withWoodcutGable(params, readings.gable, rng) : params;
  return { ...fitToSpan(read, lot.kind, width), ...lot.reconstruction?.params };
}

/* Every draw is taken before the span is read, so no other parameter moves; what a reconstruction sets stands over it. */
function fitToSpan(params: HouseParams, kind: LotKind, width: number | undefined): HouseParams {
  if (width === undefined) return params;
  const shed = kind === 'backhouse' && width < HOUSE.shedWidth;
  const pitch =
    width > HOUSE.span.maxGable
      ? Math.atan((HOUSE.span.maxGable * Math.tan(params.pitch)) / width)
      : params.pitch;
  return { ...params, pitch, ...(shed ? { storeys: 1, chimneys: [] } : {}) };
}

/** The darker half of a swatch: halfway between its tenth percentile and its median. */
function darkHalf(cls: PaletteClass): LinearRGB {
  const [a, b] = [cls.p10.linear, cls.median.linear];
  return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2];
}

/**
 * Glass (lineworkDark) and door leaves (shipHull) of every opening. The linework and wood shade
 * ramps lift sunlit infill further than plaster's (palette p90 over median 2.2 and 2.6 against
 * 1.5), so sampled swatch colours rendered windows at 0.58 of the wall's L*; the darker half of
 * each swatch brings them to the painting's 0.46. Lit by the world's own normals, leaves from
 * the darker half of shipHull still stood at 0.52 of the wall's L* where glass stood at 0.38,
 * so leaves take the swatch's darkest tenth.
 */
export function infillColors(palette: PaletteModel): {
  readonly glass: LinearRGB;
  readonly leaf: LinearRGB;
} {
  return { glass: darkHalf(palette.lineworkDark), leaf: palette.shipHull.p10.linear };
}

/**
 * A class's centres below its median are the painting's shaded parts of that surface, and the renderer
 * shades every face itself, so a wall or roof takes one of the centres at or above the median, by
 * weight: on the painter view this brings the city's dark and red pixels to the painting's
 * (docs/plans/houses.md, "District palette"), as the city wall's albedo takes its light back out of
 * the cityWall median (STYLE §8). A centre above the class's p90 is its highlights, which the renderer
 * also lights itself; `highlight` is the share of its weight it is drawn at.
 */
export function sampleLitSwatch(cls: PaletteClass, rng: Rng, highlight = 1): LinearRGB {
  const lit = cls.centres.filter((c) => luminance(c.linear) >= luminance(cls.median.linear));
  const pool = lit.length > 0 ? lit : [{ linear: cls.median.linear, weight: 1 }];
  const top = luminance(cls.p90.linear);
  return rng.weighted(
    pool.map((c) => c.linear),
    pool.map((c) => (luminance(c.linear) > top ? c.weight * highlight : c.weight))
  );
}

function hueOf(c: LinearRGB): number {
  const [, a, b] = linearToLab(c);
  return (Math.atan2(b, a) * 180) / Math.PI;
}

/** A lit centre of the front colour's class, none greener than the painting's walls (HOUSE.wallHueMax). */
export function getWallColor(colour: FrontColour, palette: PaletteModel, rng: Rng): LinearRGB {
  const cls = colour === 'light' ? palette.wallLight : palette.wallBrick;
  return sampleLitSwatch({ ...cls, centres: cls.centres.filter((c) => hueOf(c.linear) <= HOUSE.wallHueMax) }, rng);
}

export function getRoofColor(material: RoofMaterial, palette: PaletteModel, rng: Rng): LinearRGB {
  if (material === 'slate') return sampleLitSwatch(palette.roofSlate, rng);
  return sampleLitSwatch(palette.roofTile, rng, HOUSE.tileHighlight);
}

export function computeSteppedGableSteps(ridgeRise: number): number {
  const rawSteps = Math.round(ridgeRise / HOUSE.steppedGable.stepRise);
  return Math.max(HOUSE.steppedGable.minSteps, Math.min(HOUSE.steppedGable.maxSteps, rawSteps));
}
